import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  AppError,
  Button,
  Dropzone,
  EmptyState,
  Icon,
  Loader,
  Modal,
  Pagination,
  Search,
  Select,
  selectState as ss,
  SidebarOverlay,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type SelectMsg,
  type SelectState,
  type SortDescriptor,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useIssApi } from "@bfc-software/bfc-web-ui/lib/iss-api-client";

/**
 * ISS's /slots/list has no substring search on slot_id (only an exact
 * match), unlike /items/list's item_desc filter -- see ListSlots in the
 * generated ISS client. So the whole catalog (up to ISS's own max page size)
 * is fetched once per zone filter and the free-text search happens
 * client-side, the same way the design's live-filtering search box behaves.
 */
const FETCH_LIMIT = 500;
const PAGE_SIZE = 50;

type LocationsSearch = {
  q: string;
  zone: string;
  offset: number;
};

type Zone = {
  id: number;
  name: string;
};

type SlotRow = {
  slot_id: string;
  zone_id?: number | null;
  warehouse_dept?: string | null;
  aisle?: string | null;
  section_type?: string | null;
  rack_level?: number | null;
  side?: number | null;
  handstack?: string | null;
  pick_sequence: number;
  slot_status_code?: string | null;
};

type ImportRowError = { row: number; column?: string; message: string };

type ImportOutcome =
  | {
      kind: "success";
      imported: number;
      errorCount: number;
      rowErrors: ImportRowError[];
    }
  | { kind: "failed"; message: string };

function parseOffset(value: unknown): number {
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
}

/**
 * openapi-react-query throws the parsed ErrorResponse body itself
 * (`{ error, message }`) rather than an `Error` -- see the analogous helper
 * in items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/locations",
)({
  validateSearch: (search?: Record<string, unknown>): LocationsSearch => ({
    q: typeof search?.["q"] === "string" ? search["q"] : "",
    zone: typeof search?.["zone"] === "string" ? search["zone"] : "",
    offset: parseOffset(search?.["offset"]),
  }),
  component: RouteComponent,
});

type ZoneItem = ss.Item; // { id: zone name, label: zone name }

/**
 * Pure. Nothing selected = no zone filter; the placeholder says "All zones". A
 * zone in the URL is the SELECTION, whether or not the catalog has it (still
 * loading, a pasted link): it is never pushed into the list. Catalog rows are
 * `{ id: name, label: name }`, so the selection is the same shape either way and
 * no catalog lookup is needed.
 */
function zoneSelectState(a: {
  zones: ReadonlyArray<Zone>;
  isPending: boolean;
  zone: string; // `search.zone`; `""` = none
  t: TFunction;
}): SelectState<ZoneItem> {
  const base = ss.single<ZoneItem>({
    label: ss.ariaLabel(a.t("locations.filters.zoneAriaLabel")),
    placeholder: a.t("locations.filters.allZones"),
    options: a.isPending
      ? ss.loading(a.t("locations.filters.zonesLoading"))
      : ss.ready({
          items: a.zones.map((z) => ({ id: z.name, label: z.name })),
          emptyLabel: a.t("locations.filters.noZones"),
        }),
  });
  return a.zone === ""
    ? base
    : ss.withPick<ZoneItem, typeof base>(base, { id: a.zone, label: a.zone });
}

function LocationTableRow({
  row,
  zoneName,
  onOpen,
}: {
  row: SlotRow;
  zoneName: string | null;
  onOpen: (row: SlotRow) => void;
}) {
  return (
    <TableRow data-test-role="location-row" onAction={() => onOpen(row)}>
      <TableCell>
        <span className="font-strong">{row.slot_id}</span>
      </TableCell>
      <TableCell>
        {zoneName ? (
          <Tag label={zoneName} display={{ tone: "auto" }} />
        ) : (
          <span className="text-on-surface-variant">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function ImportModal({
  onImport,
  isPending,
  t,
}: {
  onImport: (file: File) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  return (
    <Modal
      control="trigger"
      data-test-role="locations-import-modal"
      trigger={{
        kind: "text-icon-left",
        icon: "UploadCloud",
        variant: "avatar-surface",
        text: t("locations.import.button"),
      }}
      header={<strong>{t("locations.import.modalTitle")}</strong>}
    >
      {({ close }) => (
        <ImportModalBody
          onImport={onImport}
          isPending={isPending}
          close={close}
          t={t}
        />
      )}
    </Modal>
  );
}

/**
 * The picked file lives here rather than in `ImportModal` so that it unmounts
 * with the dialog: `Modal` owns dismissal now (its own close button included),
 * and a file left over from a dismissed upload would otherwise still be named
 * in the dropzone the next time the modal opened.
 */
function ImportModalBody({
  onImport,
  isPending,
  close,
  t,
}: {
  onImport: (file: File) => Promise<void>;
  isPending: boolean;
  close: () => void;
  t: TFunction;
}) {
  const [file, setFile] = useState<File | null>(null);

  return (
    <>
      <Dropzone
        acceptedFileTypes={[".csv"]}
        onSelect={(files) => setFile(files[0] ?? null)}
      >
        <Icon name="UploadCloud" size={24} />
        <strong>{file ? file.name : t("locations.import.chooseFile")}</strong>
        <span>{t("locations.import.dropHint")}</span>
      </Dropzone>

      <div className="flex justify-end gap-8">
        <Button
          kind="text-only"
          variant="secondary"
          text={t("locations.import.cancel")}
          isDisabled={isPending}
          onPress={close}
        />
        <span className="flex items-center gap-8">
          {/* Pallet has no icon-spin utility (no `animate-*` namespace is
              wired - see utilities.css's note that neither Tailwind pass
              loads its default theme). `Loader` is the pending affordance
              pallet does ship, so it stands in for the bespoke spinning
              `Icon` rather than reinventing the animation locally. */}
          {isPending ? <Loader type="inline" /> : null}
          <Button
            kind="text-only"
            variant="avatar-surface"
            text={
              isPending
                ? t("locations.import.importing")
                : t("locations.import.upload")
            }
            isDisabled={!file || isPending}
            onPress={() => {
              if (!file) return;
              onImport(file).then(close);
            }}
          />
        </span>
      </div>
    </>
  );
}

function SlotDrawer({
  row,
  zoneName,
  onClose,
  t,
}: {
  row: SlotRow;
  zoneName: string | null;
  onClose: () => void;
  t: TFunction;
}) {
  const none = t("locations.drawer.none");

  return (
    // `isOpen` is a constant for the same reason it is in `items.tsx`'s
    // `ItemDrawer`: the caller renders this only for a selected row, so that
    // selection is the single answer to "is it open" and `onOpenChange` routes
    // every dismissal back to it.
    <SidebarOverlay
      control="external"
      isOpen={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
      header={
        <>
          <h2 className="drawer-title">{row.slot_id}</h2>
          <span className="drawer-subtitle">
            {[zoneName, row.section_type].filter(Boolean).join(" · ") || none}
          </span>
        </>
      }
    >
      <div className="drawer-body">
        <div className="section gap-8">
          <div className="section-heading">
            {t("locations.drawer.slotAttributes")}
          </div>
          <dl className="detail-grid">
            <div>
              <dt>{t("locations.table.slot")}</dt>
              <dd>{row.slot_id}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.zone")}</dt>
              <dd>{zoneName ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.locationType")}</dt>
              <dd>{row.section_type ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.warehouseDept")}</dt>
              <dd>{row.warehouse_dept ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.aisle")}</dt>
              <dd>{row.aisle ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.rackLevel")}</dt>
              <dd>{row.rack_level ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.side")}</dt>
              <dd>{row.side ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.handstack")}</dt>
              <dd>{row.handstack ?? none}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.pickSequence")}</dt>
              <dd>{row.pick_sequence}</dd>
            </div>
            <div>
              <dt>{t("locations.drawer.status")}</dt>
              <dd>{row.slot_status_code ?? none}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="drawer-footer">
        <span>{t("locations.drawer.readOnlyNotice")}</span>
        <Button
          kind="text-only"
          variant="secondary"
          text={t("locations.drawer.edit")}
          isDisabled={true}
        />
      </div>
    </SidebarOverlay>
  );
}

function ImportBanner({
  outcome,
  onDismiss,
  t,
}: {
  outcome: ImportOutcome;
  onDismiss: () => void;
  t: TFunction;
}) {
  if (outcome.kind === "failed") {
    return (
      <Alert
        severity="error"
        title={t("locations.import.failed", { message: outcome.message })}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <Alert
      severity={outcome.errorCount > 0 ? "warning" : "success"}
      title={t("locations.import.successCount", { count: outcome.imported })}
      onDismiss={onDismiss}
    >
      {outcome.errorCount > 0 ? (
        <div>
          {t("locations.import.errorCount", { count: outcome.errorCount })}
        </div>
      ) : null}
      {outcome.rowErrors.length > 0 ? (
        <div className="text-size-13">
          <strong>{t("locations.import.errorsHeading")}</strong>
          <ul className="mt-4 pl-20">
            {outcome.rowErrors.map((rowError, index) => (
              <li key={`${rowError.row}-${rowError.column ?? index}`}>
                {t("locations.import.rowError", {
                  row: rowError.row,
                  message: rowError.column
                    ? `${rowError.column}: ${rowError.message}`
                    : rowError.message,
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Alert>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useIssApi();
  const [importOutcome, setImportOutcome] = useState<ImportOutcome | null>(
    null,
  );
  const [openRow, setOpenRow] = useState<SlotRow | null>(null);
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending",
  );

  const goTo = (next: Partial<LocationsSearch>) => {
    navigate({
      search: (prev: LocationsSearch) => ({ ...prev, offset: 0, ...next }),
      resetScroll: false,
    });
  };

  const zonesQuery = client.useQuery("get", "/zones/all", {});
  const slotsQuery = client.useQuery(
    "get",
    "/slots/list",
    {
      params: {
        query: {
          limit: FETCH_LIMIT,
          offset: 0,
          ...(search.zone ? { zoneName: search.zone } : {}),
        },
      },
    },
    { placeholderData: (previousData) => previousData },
  );

  const importMutation = client.useMutation("post", "/slots/import");

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const zoneNameById = new Map(zones.map((zone) => [zone.id, zone.name]));
  const slots = (slotsQuery.data?.data ?? []) as SlotRow[];

  const trimmedQuery = search.q.trim().toLowerCase();
  const filtered = trimmedQuery
    ? slots.filter((slot) => slot.slot_id.toLowerCase().includes(trimmedQuery))
    : slots;

  const sorted = [...filtered].sort((a, b) =>
    sortDir === "ascending"
      ? a.slot_id.localeCompare(b.slot_id)
      : b.slot_id.localeCompare(a.slot_id),
  );

  const total = sorted.length;
  const pageRows = sorted.slice(search.offset, search.offset + PAGE_SIZE);
  const currentPage = Math.floor(search.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total > 0 ? search.offset + 1 : 0;
  const rangeEnd = total > 0 ? search.offset + pageRows.length : 0;

  const totalOnServer = slotsQuery.data?.pagination.total_count ?? 0;
  const isTruncated = totalOnServer > slots.length;

  const sortDescriptor: SortDescriptor = {
    column: "slot_id",
    direction: sortDir,
  };

  const onZoneMsg = (msg: SelectMsg<ZoneItem>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) => goTo({ zone: String(item.id) }))
      .with({ kind: "cleared" }, () => goTo({ zone: "" })) // the `x`: back to all zones
      // no search; `opened`/`closed` carry nothing here
      .otherwise(() => {});

  const isLoading = slotsQuery.isLoading || zonesQuery.isLoading;
  const isError = slotsQuery.isError || zonesQuery.isError;

  const handleImport = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return importMutation
      .mutateAsync({ body: formData as unknown as { file: string } })
      .then((response) => {
        setImportOutcome({
          kind: "success",
          imported: response.imported_count,
          errorCount: response.error_count,
          rowErrors: response.errors ?? [],
        });
        slotsQuery.refetch();
      })
      .catch((error: unknown) => {
        setImportOutcome({
          kind: "failed",
          // TODO: SR-301 phase 7 — this fallback renders "Import failed:
          // Dismiss". `locations.import.dismiss` is the wrong key here; it survived
          // the Alert migration only because this reference keeps it from
          // being an orphan. Picking the right string is a copy change, not a
          // styling one, so it is left for its owner.
          message: errorMessage(error, t("locations.import.dismiss")),
        });
      });
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("locations.heading")}</h1>
            <p className="page-subtitle">{t("locations.subheading")}</p>
          </div>
          <div className="page-header-right">
            <div className="page-header-searches">
              <Search
                aria-label={t("locations.filters.searchAriaLabel")}
                placeholder={t("locations.filters.searchPlaceholder")}
                value={search.q}
                onChange={(value) => goTo({ q: String(value) })}
                onClear={() => goTo({ q: "" })}
              />
            </div>
            <div className="page-header-actions">
              <Select
                state={zoneSelectState({
                  zones,
                  isPending: zonesQuery.isPending,
                  zone: search.zone,
                  t,
                })}
                onMsg={onZoneMsg}
                display={{ trigger: { minWidth: "150px" } }}
              />

              <ImportModal
                onImport={handleImport}
                isPending={importMutation.isPending}
                t={t}
              />
            </div>
          </div>
        </header>

        {importOutcome ? (
          <ImportBanner
            outcome={importOutcome}
            onDismiss={() => setImportOutcome(null)}
            t={t}
          />
        ) : null}

        {!isLoading && !isError && isTruncated ? (
          <Alert
            severity="warning"
            title={t("locations.truncatedNotice", {
              shown: slots.length,
              total: totalOnServer,
            })}
          />
        ) : null}

        <div className="table-frame">
          {isLoading ? (
            <Loader type="page" />
          ) : isError ? (
            <AppError
              description={t("locations.failed")}
              resolution={t("locations.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("locations.table.ariaLabel")}
              isResizable={false}
              sortDescriptor={sortDescriptor}
              onSortChange={(descriptor) =>
                setSortDir(
                  descriptor.direction === "descending"
                    ? "descending"
                    : "ascending",
                )
              }
            >
              <TableHead>
                <TableHeaderCell
                  id="slot_id"
                  isRowHeader={true}
                  isSortable={true}
                >
                  {t("locations.table.slot")}
                </TableHeaderCell>
                <TableHeaderCell>{t("locations.table.zone")}</TableHeaderCell>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <EmptyState heading={t("locations.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((row) => (
                    <LocationTableRow
                      key={row.slot_id}
                      row={row}
                      zoneName={
                        row.zone_id != null
                          ? (zoneNameById.get(row.zone_id) ?? null)
                          : null
                      }
                      onOpen={setOpenRow}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {!isLoading && !isError && total > 0 ? (
          <div className="flex items-center justify-between gap-12 p-4">
            <span className="text-size-14 text-on-surface-variant">
              {t("locations.rangeSummary", {
                start: rangeStart,
                end: rangeEnd,
                total,
              })}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              nextButton={{
                isDisabled: search.offset + pageRows.length >= total,
                onPress: () =>
                  navigate({
                    search: (prev: LocationsSearch) => ({
                      ...prev,
                      offset: prev.offset + PAGE_SIZE,
                    }),
                  }),
              }}
              previousButton={{
                isDisabled: search.offset <= 0,
                onPress: () =>
                  navigate({
                    search: (prev: LocationsSearch) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset - PAGE_SIZE),
                    }),
                  }),
              }}
              jumpTo={(page) =>
                navigate({
                  search: (prev: LocationsSearch) => ({
                    ...prev,
                    offset: Math.max(0, (page - 1) * PAGE_SIZE),
                  }),
                })
              }
            />
          </div>
        ) : null}
      </div>

      {openRow ? (
        <SlotDrawer
          row={openRow}
          zoneName={
            openRow.zone_id != null
              ? (zoneNameById.get(openRow.zone_id) ?? null)
              : null
          }
          onClose={() => setOpenRow(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}
